<template>
  <div class="flex gap-4 p-4 h-full">
    <!-- COLUMN 1: Menu -->
    <div class="flex-1 flex flex-col overflow-y-auto pr-2">
      <!-- Column Header -->
      <div class="flex-shrink-0 mb-4">
        <h3 class="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
          <span class="flex items-center justify-center w-7 h-7 rounded-full bg-indigo-600 text-white text-sm font-bold mr-2">1</span>
          {{ $t('app.views.orders.modals.new_order.tabs.menu_items') }}
        </h3>
      </div>

      <div class="flex-1 space-y-4 overflow-y-auto px-3">
        <!-- Item Options Inline - Show when selecting item -->
        <ItemOptionsInline
          v-if="showingItemOptions && selectedItem"
          :item="selectedItem"
          :top-notes="topNotes"
          :get-variant-price="(variant) => selectedItem ? getVariantPrice(selectedItem, variant) : 0"
          :order-type="form.type"
          @back="$emit('back-to-menu')"
          @add="$emit('add-item', $event)"
        />

        <!-- Normal Menu Content - Show when not selecting item -->
        <template v-if="!showingItemOptions || !selectedItem">
          <!-- Order Type Selector -->
          <OrderTypeSelector
            :model-value="form"
            :tables="availableTables"
            :loading="loading.tables"
            :error="error.tables"
            :allow-dine-in-without-table="restaurantSettings.allowDineInWithoutTable.value"
            @update:model-value="$emit('update:form', $event)"
          />

          <!-- Customer Selection for Delivery and Takeaway (optional for takeaway when setting is enabled) -->
          <div
            v-if="form.type === 'Delivery' || form.type === 'Takeaway'"
            class="mb-4"
          >
            <CustomerSelectorWrapper 
              :model-value="selectedCustomer"
              :order-type="form.type as 'Takeaway' | 'Delivery'"
              :initial-selected-address-id="selectedAddressId"
              @update:model-value="$emit('update:selected-customer', $event)"
              @create-customer="$emit('create-customer')"
              @address-selected="$emit('address-selected', $event)"
            />
          </div>

          
          <!-- Persons Manager -->
          <PersonsManager
            :order-type="form.type as 'dine_in' | 'takeaway' | 'delivery'"
            :persons="persons"
            :active-person-index="activePersonIndex"
            :disabled="isEditMode"
            @update:active-person-index="$emit('update:active-person-index', $event)"
            @add-person="$emit('add-person')" 
            @remove-person="$emit('remove-person', $event)"
            @update-person-name="$emit('update-person-name', $event)"
          />

          <!-- Active Diner Items Summary -->
          <DinerItemsSummary
            v-if="persons[activePersonIndex]"
            :title="persons[activePersonIndex].name || $t('app.views.orders.modals.new_order.persons.person_label', { position: persons[activePersonIndex].position })"
            :items="persons[activePersonIndex].items" 
            :get-menu-item-name="getMenuItemName"
            :get-menu-item-category="getMenuItemCategory"
            :empty-message="$t('app.views.orders.modals.new_order.persons.no_items')"
            @remove-group="$emit('remove-group', $event)"
            @update-quantity="(group, newQuantity) => $emit('update-quantity', group, newQuantity)"
          />

          <!-- Menu Items Selector - Buttons View -->
          <MenuItemsSelectorButtons
            ref="menuSelectorRef"
            :loading="loading.menu"
            :error="error.menu"
            :category-names="categoryNames"
            :menu-items-by-category="menuItemsByCategory"
            :get-item-quantity="getItemQuantity"
            @select-item="$emit('select-item', $event)"
          />
        </template>
      </div>
    </div>

    <!-- COLUMN 2: Order Summary -->
    <div class="flex-1 flex flex-col overflow-y-auto px-2 border-l border-gray-200 dark:border-gray-700">
      <!-- Column Header -->
      <div class="flex-shrink-0 mb-4">
        <h3 class="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
          <span class="flex items-center justify-center w-7 h-7 rounded-full bg-indigo-600 text-white text-sm font-bold mr-2">2</span>
          {{ $t('app.views.orders.modals.new_order.tabs.summary') }}
        </h3>
      </div>

      <div class="flex-1 overflow-y-auto">
        <OrderSummary 
          :use-multiple-diners="true" 
          :persons="persons"
          :selected-items="selectedItems" 
          :get-menu-item-name="getMenuItemName"
          :get-menu-item-category="getMenuItemCategory"
          :calculate-item-total="calculateItemTotal" 
          :is-item-locked="isItemLocked"
          :show-create-button="false"
          :iva-enabled="ivaEnabled"
          :iva-rate="ivaRate"
          :iva-amount="ivaAmount"
          :subtotal="subtotal"
          :order-type="form.type as 'dine_in' | 'takeaway' | 'delivery'"
          :selected-customer="selectedCustomer"
          :selected-address="selectedAddress"
          :customer-name="form.customerName"
          :delivery-address="getDeliveryAddressFromForm()"
          :delivery-fee="deliveryFee"
          :current-discount="currentDiscount"
          :final-total="finalTotal"
          :applied-promotions="appliedPromotions"
          :promotion-messages="promotionMessages"
          :is-calculating-promotions="isCalculatingPromotions"
          :order-notes="form.notes"
        />
      </div>
    </div>

    <!-- COLUMN 3: Payment & Create Order -->
    <div class="flex-1 flex flex-col overflow-y-auto pl-2 border-l border-gray-200 dark:border-gray-700">
      <!-- Column Header -->
      <div class="flex-shrink-0 mb-4">
        <h3 class="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
          <span class="flex items-center justify-center w-7 h-7 rounded-full bg-indigo-600 text-white text-sm font-bold mr-2">3</span>
          Finalizar
        </h3>
      </div>

      <div class="flex-1 overflow-y-auto">
        <!-- Order Notes -->
        <Notes 
          :model-value="form.notes"
          :label="$t('app.views.orders.modals.new_order.order_notes')"
          :placeholder="$t('app.views.orders.modals.new_order.notes_placeholder')"
          textarea-id="order-notes"
          @update:model-value="$emit('update:form-notes', $event)"
        />

        <!-- Payment Section -->
        <PaymentSection
          ref="paymentSectionRef"
          :is-edit-mode="isEditMode"
          :can-process-payments="canProcessPayments"
          :order-total="finalTotal"
          :mark-as-paid="markAsPaid"
          :selected-payment-method="selectedPaymentMethod"
          :cash-received="cashReceived"
          :disable-toggle="isPosOnlyMode && modeLoaded"
          :hide-title="true"
          :iva-enabled="ivaEnabled"
          :iva-rate="ivaRate"
          :subtotal="subtotal"
          :iva-amount="ivaAmount"
          :order-type="form.type as 'dine_in' | 'takeaway' | 'delivery'"
          :delivery-fee="deliveryFee"
          :current-discount="currentDiscount"
          :applied-promotions="appliedPromotions"
          @update:mark-as-paid="$emit('update:mark-as-paid', $event)"
          @update:selected-payment-method="$emit('update:selected-payment-method', $event)"
          @update:cash-received="$emit('update:cash-received', $event)"
          @toggle-iva="$emit('toggle-iva', $event)"
        />
      </div>

      <!-- Create Order Button - Always at bottom -->
      <div class="flex-shrink-0 pt-6 pb-2">
        <button
          type="button"
          class="w-full inline-flex justify-center rounded-lg bg-indigo-600 px-4 py-3 text-base font-semibold text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          :disabled="!hasItems || !isPaymentValid || ((form.type === 'Dine-in' && !form.tableId && !restaurantSettings.allowDineInWithoutTable.value) || !isCustomerSelectionValid)"
          @click="$emit('create-order')"
        >
          {{ isEditMode ? $t('app.views.orders.modals.new_order.update_order') : $t('app.views.orders.modals.new_order.create_order') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAuthStore } from '@/stores/auth';

// Import components
import ItemOptionsInline from './ItemOptionsInline.vue';
import OrderTypeSelector from './OrderTypeSelector.vue';
import CustomerSelectorWrapper from './CustomerSelectorWrapper.vue';
import PersonsManager from './PersonsManager.vue';
import DinerItemsSummary from './DinerItemsSummary.vue';
import MenuItemsSelectorButtons from './MenuItemsSelectorButtons.vue';
import OrderSummary from './OrderSummary.vue';
import Notes from './Notes.vue';
import PaymentSection from './PaymentSection.vue';

// Props
defineProps<{
  // Form data
  form: any;
  // UI state
  showingItemOptions: boolean;
  selectedItem: any;
  topNotes: string[];
  // Persons
  persons: any[];
  activePersonIndex: number;
  isEditMode: boolean;
  // Data
  availableTables: any[];
  loading: any;
  error: any;
  categoryNames: string[];
  menuItemsByCategory: any;
  selectedItems: any[];
  // Calculations
  ivaEnabled: boolean;
  ivaRate: number;
  ivaAmount: number;
  subtotal: number;
  deliveryFee: number;
  currentDiscount: number;
  finalTotal: number;
  appliedPromotions: any[];
  promotionMessages: string[];
  isCalculatingPromotions: boolean;
  // Customer
  selectedCustomer: any;
  selectedAddress: any;
  selectedAddressId: any;
  isCustomerSelectionValid: boolean;
  // Payment
  markAsPaid: boolean;
  selectedPaymentMethod: string;
  cashReceived: number;
  isPaymentValid: boolean;
  hasItems: boolean;
  canProcessPayments: boolean;
  // Mode
  isPosOnlyMode: boolean;
  modeLoaded: boolean;
  // Helpers
  getMenuItemName: (id: number) => string;
  getMenuItemCategory: (id: number) => string;
  getItemQuantity: (menuItemId: number | string, variantId?: number | string | null) => number;
  calculateItemTotal: (item: any) => string;
  isItemLocked: (item: any) => boolean;
  getVariantPrice: (item: any, variant: any) => number;
  getDeliveryAddressFromForm: () => string;
  restaurantSettings: any;
}>();

// Emits
defineEmits<{
  'back-to-menu': [];
  'add-item': [data: any];
  'create-customer': [];
  'address-selected': [address: any];
  'update:active-person-index': [index: number];
  'add-person': [];
  'remove-person': [index: number];
  'update-person-name': [data: any];
  'remove-group': [group: any];
  'update-quantity': [group: any, newQuantity: number];
  'select-item': [item: any];
  'toggle-iva': [enabled: boolean];
  'create-order': [];
  'update:form': [form: any];
  'update:selected-customer': [customer: any];
  'update:form-notes': [notes: string];
  'update:mark-as-paid': [paid: boolean];
  'update:selected-payment-method': [method: string];
  'update:cash-received': [amount: number];
}>();

// Refs
const menuSelectorRef = ref<InstanceType<typeof MenuItemsSelectorButtons> | null>(null);
const paymentSectionRef = ref<InstanceType<typeof PaymentSection> | null>(null);

// Composables
const { t } = useI18n();
const authStore = useAuthStore();

// Expose refs for parent
defineExpose({
  menuSelectorRef,
  paymentSectionRef
});
</script>
